import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Download,
  Upload,
  Settings2,
  GripVertical,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";

// ── Types ──────────────────────────────────────────────────────────────────

interface FieldDefinition {
  id: string;
  client_id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface FieldValue {
  id: string;
  profile_id: string;
  field_definition_id: string;
  field_value: string | null;
}

interface ProfileInfo {
  id: string;
  full_name: string | null;
  email: string | null;
}

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/No" },
  { value: "profile_link", label: "Profile Link" },
];

// ── Component ──────────────────────────────────────────────────────────────

const CustomUserFields = () => {
  const { profile: currentProfile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [definitions, setDefinitions] = useState<FieldDefinition[]>([]);
  const [allValues, setAllValues] = useState<FieldValue[]>([]);
  const [profiles, setProfiles] = useState<ProfileInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Add/Edit field definition dialog
  const [defDialogOpen, setDefDialogOpen] = useState(false);
  const [editingDef, setEditingDef] = useState<FieldDefinition | null>(null);
  const [defName, setDefName] = useState("");
  const [defLabel, setDefLabel] = useState("");
  const [defType, setDefType] = useState("text");
  const [defRequired, setDefRequired] = useState(false);
  const [defSaving, setDefSaving] = useState(false);

  // Values table editing (inline)
  const [editingValues, setEditingValues] = useState<Record<string, Record<string, string>>>({});
  const [savingValues, setSavingValues] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────

  const fetchDefinitions = async () => {
    if (!currentProfile?.client_id) return;

    const { data, error } = await (supabase
      .from("user_custom_field_definitions" as any)
      .select("*")
      .eq("client_id", currentProfile.client_id)
      .order("sort_order", { ascending: true }) as any);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setDefinitions(data ?? []);
    }
  };

  const fetchValues = async () => {
    if (!currentProfile?.client_id || definitions.length === 0) {
      setAllValues([]);
      return;
    }

    const defIds = definitions.map((d) => d.id);
    const { data, error } = await (supabase
      .from("user_custom_field_values" as any)
      .select("id, profile_id, field_definition_id, field_value")
      .in("field_definition_id", defIds) as any);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setAllValues(data ?? []);
    }
  };

  const fetchProfiles = async () => {
    if (!currentProfile?.client_id) return;

    const { data, error } = await (supabase
      .from("profiles")
      .select("id, full_name, email")
      .eq("client_id", currentProfile.client_id)
      .order("full_name", { ascending: true }) as any);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setProfiles(data ?? []);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchDefinitions();
      await fetchProfiles();
      setLoading(false);
    };
    load();
  }, [currentProfile?.client_id]);

  useEffect(() => {
    if (definitions.length > 0) {
      fetchValues();
    }
  }, [definitions]);

  // Build a lookup: profileId → { fieldDefId → value }
  useEffect(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const p of profiles) {
      map[p.id] = {};
      for (const d of definitions) {
        const val = allValues.find(
          (v) => v.profile_id === p.id && v.field_definition_id === d.id
        );
        map[p.id][d.id] = val?.field_value ?? "";
      }
    }
    setEditingValues(map);
  }, [profiles, definitions, allValues]);

  // ── Field definition CRUD ──────────────────────────────────────────────

  const openAddDefDialog = () => {
    setEditingDef(null);
    setDefName("");
    setDefLabel("");
    setDefType("text");
    setDefRequired(false);
    setDefDialogOpen(true);
  };

  const openEditDefDialog = (def: FieldDefinition) => {
    setEditingDef(def);
    setDefName(def.field_name);
    setDefLabel(def.field_label);
    setDefType(def.field_type);
    setDefRequired(def.is_required);
    setDefDialogOpen(true);
  };

  const handleSaveDef = async () => {
    if (!defName.trim() || !defLabel.trim()) {
      toast({ title: "Error", description: "Field name and label are required.", variant: "destructive" });
      return;
    }

    // Sanitize field_name to be machine-readable
    const sanitizedName = defName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

    if (!sanitizedName) {
      toast({ title: "Error", description: "Field name must contain at least one alphanumeric character.", variant: "destructive" });
      return;
    }

    setDefSaving(true);
    try {
      if (editingDef) {
        const { error } = await (supabase
          .from("user_custom_field_definitions" as any)
          .update({
            field_name: sanitizedName,
            field_label: defLabel.trim(),
            field_type: defType,
            is_required: defRequired,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingDef.id) as any);
        if (error) throw error;
        toast({ title: "Updated", description: `Field "${defLabel.trim()}" updated.` });
      } else {
        const maxOrder = definitions.length > 0 ? Math.max(...definitions.map((d) => d.sort_order)) : -1;
        const { error } = await (supabase
          .from("user_custom_field_definitions" as any)
          .insert({
            client_id: currentProfile!.client_id,
            field_name: sanitizedName,
            field_label: defLabel.trim(),
            field_type: defType,
            is_required: defRequired,
            sort_order: maxOrder + 1,
          }) as any);
        if (error) throw error;
        toast({ title: "Created", description: `Field "${defLabel.trim()}" added.` });
      }
      setDefDialogOpen(false);
      await fetchDefinitions();
    } catch (err: any) {
      const msg = err.message?.includes("duplicate key")
        ? `A field with the name "${sanitizedName}" already exists.`
        : err.message;
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
    setDefSaving(false);
  };

  const handleDeleteDef = async (def: FieldDefinition) => {
    if (!confirm(`Delete custom field "${def.field_label}"? All user values for this field will be removed.`)) return;

    const { error } = await (supabase
      .from("user_custom_field_definitions" as any)
      .delete()
      .eq("id", def.id) as any);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: `Field "${def.field_label}" removed.` });
      await fetchDefinitions();
    }
  };

  // ── Save field values ──────────────────────────────────────────────────

  const handleSaveValues = async () => {
    setSavingValues(true);
    try {
      // Build upsert rows for all profile/field combinations that have a value
      const upsertRows: { profile_id: string; field_definition_id: string; field_value: string | null }[] = [];
      const deleteKeys: { profile_id: string; field_definition_id: string }[] = [];

      for (const profileId of Object.keys(editingValues)) {
        for (const defId of Object.keys(editingValues[profileId])) {
          const val = editingValues[profileId][defId];
          if (val !== undefined && val !== "") {
            upsertRows.push({
              profile_id: profileId,
              field_definition_id: defId,
              field_value: val,
            });
          } else {
            deleteKeys.push({ profile_id: profileId, field_definition_id: defId });
          }
        }
      }

      // Upsert values using individual operations (supabase upsert on composite unique)
      for (const row of upsertRows) {
        // Check if exists
        const { data: existing } = await (supabase
          .from("user_custom_field_values" as any)
          .select("id")
          .eq("profile_id", row.profile_id)
          .eq("field_definition_id", row.field_definition_id)
          .maybeSingle() as any);

        if (existing) {
          await (supabase
            .from("user_custom_field_values" as any)
            .update({ field_value: row.field_value, updated_at: new Date().toISOString() })
            .eq("id", existing.id) as any);
        } else {
          await (supabase
            .from("user_custom_field_values" as any)
            .insert(row) as any);
        }
      }

      // Delete empty values
      for (const key of deleteKeys) {
        await (supabase
          .from("user_custom_field_values" as any)
          .delete()
          .eq("profile_id", key.profile_id)
          .eq("field_definition_id", key.field_definition_id) as any);
      }

      toast({ title: "Saved", description: "All custom field values saved." });
      await fetchValues();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSavingValues(false);
  };

  // ── CSV Export ──────────────────────────────────────────────────────────

  const handleExport = () => {
    if (definitions.length === 0 || profiles.length === 0) {
      toast({ title: "Nothing to export", description: "Add custom fields and users first.", variant: "destructive" });
      return;
    }

    // Build CSV: email, full_name, field1, field2, ...
    const headers = ["email", "full_name", ...definitions.map((d) => d.field_name)];
    const rows = profiles.map((p) => {
      const vals = definitions.map((d) => {
        const v = editingValues[p.id]?.[d.id] ?? "";
        // For profile_link fields, try to resolve to email
        if (d.field_type === "profile_link" && v) {
          const linkedProfile = profiles.find((pp) => pp.id === v);
          return linkedProfile?.email ?? v;
        }
        return v;
      });
      return [p.email ?? "", p.full_name ?? "", ...vals];
    });

    const csvContent = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `custom_user_fields_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "Exported", description: `CSV exported with ${profiles.length} users and ${definitions.length} custom fields.` });
  };

  // ── CSV Import ──────────────────────────────────────────────────────────

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").map((l) => l.trim()).filter((l) => l);
      if (lines.length < 2) {
        toast({ title: "Error", description: "CSV must have a header row and at least one data row.", variant: "destructive" });
        setImporting(false);
        return;
      }

      // Parse header
      const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = "";
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
              current += '"';
              i++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (ch === "," && !inQuotes) {
            result.push(current);
            current = "";
          } else {
            current += ch;
          }
        }
        result.push(current);
        return result;
      };

      const headers = parseCSVLine(lines[0]);
      const emailIdx = headers.findIndex((h) => h.toLowerCase() === "email");
      if (emailIdx === -1) {
        toast({ title: "Error", description: "CSV must have an 'email' column to identify users.", variant: "destructive" });
        setImporting(false);
        return;
      }

      // Identify custom field columns (everything except 'email' and 'full_name')
      const skipCols = new Set(["email", "full_name"]);
      const fieldColumns: { colIdx: number; fieldName: string }[] = [];
      for (let i = 0; i < headers.length; i++) {
        const normalized = headers[i].toLowerCase().trim().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
        if (!skipCols.has(normalized) && normalized) {
          fieldColumns.push({ colIdx: i, fieldName: normalized });
        }
      }

      // Auto-create missing field definitions
      const existingNames = new Set(definitions.map((d) => d.field_name));
      let maxOrder = definitions.length > 0 ? Math.max(...definitions.map((d) => d.sort_order)) : -1;
      const newDefs: { field_name: string; field_label: string }[] = [];

      for (const fc of fieldColumns) {
        if (!existingNames.has(fc.fieldName)) {
          maxOrder++;
          newDefs.push({ field_name: fc.fieldName, field_label: headers[fc.colIdx].trim() });
          const { error } = await (supabase
            .from("user_custom_field_definitions" as any)
            .insert({
              client_id: currentProfile!.client_id,
              field_name: fc.fieldName,
              field_label: headers[fc.colIdx].trim(),
              field_type: "text",
              is_required: false,
              sort_order: maxOrder,
            }) as any);
          if (error && !error.message?.includes("duplicate key")) {
            console.error("Failed to create field definition:", error);
          }
        }
      }

      // Re-fetch definitions to get all IDs (including newly created ones)
      await fetchDefinitions();

      // Need fresh definitions - fetch them directly
      const { data: freshDefs } = await (supabase
        .from("user_custom_field_definitions" as any)
        .select("*")
        .eq("client_id", currentProfile!.client_id)
        .order("sort_order", { ascending: true }) as any);

      const defMap: Record<string, string> = {}; // field_name → id
      for (const d of (freshDefs ?? []) as FieldDefinition[]) {
        defMap[d.field_name] = d.id;
      }

      // Build email → profile lookup
      const emailToProfile: Record<string, string> = {};
      for (const p of profiles) {
        if (p.email) emailToProfile[p.email.toLowerCase()] = p.id;
      }

      // Process data rows
      let imported = 0;
      let skipped = 0;

      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        const email = cols[emailIdx]?.toLowerCase().trim();
        if (!email) { skipped++; continue; }

        const profileId = emailToProfile[email];
        if (!profileId) { skipped++; continue; }

        for (const fc of fieldColumns) {
          const val = cols[fc.colIdx]?.trim() ?? "";
          const defId = defMap[fc.fieldName];
          if (!defId) continue;

          if (val) {
            // Resolve profile_link fields: if the value looks like an email, resolve to profile ID
            let resolvedVal = val;
            const fieldDef = (freshDefs as FieldDefinition[])?.find((d) => d.id === defId);
            if (fieldDef?.field_type === "profile_link" && val.includes("@")) {
              const linkedId = emailToProfile[val.toLowerCase()];
              if (linkedId) resolvedVal = linkedId;
            }

            // Upsert
            const { data: existing } = await (supabase
              .from("user_custom_field_values" as any)
              .select("id")
              .eq("profile_id", profileId)
              .eq("field_definition_id", defId)
              .maybeSingle() as any);

            if (existing) {
              await (supabase
                .from("user_custom_field_values" as any)
                .update({ field_value: resolvedVal, updated_at: new Date().toISOString() })
                .eq("id", existing.id) as any);
            } else {
              await (supabase
                .from("user_custom_field_values" as any)
                .insert({
                  profile_id: profileId,
                  field_definition_id: defId,
                  field_value: resolvedVal,
                }) as any);
            }
            imported++;
          }
        }
      }

      toast({
        title: "Import Complete",
        description: `${imported} field value(s) imported. ${skipped} row(s) skipped (email not found).${newDefs.length > 0 ? ` ${newDefs.length} new field(s) created: ${newDefs.map((d) => d.field_label).join(", ")}.` : ""}`,
      });

      await fetchDefinitions();
      await fetchValues();
    } catch (err: any) {
      toast({ title: "Import Error", description: err.message, variant: "destructive" });
    }
    setImporting(false);
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Update a single cell ──────────────────────────────────────────────

  const updateCellValue = (profileId: string, defId: string, value: string) => {
    setEditingValues((prev) => ({
      ...prev,
      [profileId]: {
        ...(prev[profileId] ?? {}),
        [defId]: value,
      },
    }));
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentProfile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <Settings2 className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client assigned to your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Custom User Fields</h1>
          <p className="text-sm text-muted-foreground">
            Define custom fields for users (e.g. manager, city, zone, department) and manage values. These fields are available in chat queries and triggers.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExport} className="gap-2" disabled={definitions.length === 0}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
            disabled={importing}
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleImport}
          />
          <Button onClick={openAddDefDialog} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Field
          </Button>
        </div>
      </div>

      {/* Field Definitions Section */}
      <div>
        <h2 className="text-lg font-medium mb-3">Field Definitions</h2>
        {definitions.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-12">
            <Settings2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No custom fields defined yet.</p>
            <p className="text-sm text-muted-foreground">
              Click "Add Field" to define fields like manager_id, city, department, etc.
            </p>
          </Card>
        ) : (
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Field Name</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Required</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {definitions.map((def, idx) => (
                  <TableRow key={def.id}>
                    <TableCell className="text-muted-foreground">
                      <GripVertical className="h-4 w-4 inline" />
                      {idx + 1}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{def.field_name}</TableCell>
                    <TableCell>{def.field_label}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {FIELD_TYPES.find((t) => t.value === def.field_type)?.label ?? def.field_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {def.is_required ? (
                        <Badge variant="default" className="text-[10px]">Yes</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDefDialog(def)}
                          title="Edit field"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteDef(def)}
                          title="Delete field"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* User Values Section */}
      {definitions.length > 0 && profiles.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-medium">User Field Values</h2>
            <Button onClick={handleSaveValues} disabled={savingValues} className="gap-2">
              {savingValues && <Loader2 className="h-4 w-4 animate-spin" />}
              Save All Values
            </Button>
          </div>
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-card z-10">User</TableHead>
                  <TableHead className="sticky left-0 bg-card z-10">Email</TableHead>
                  {definitions.map((def) => (
                    <TableHead key={def.id} className="min-w-[150px]">
                      {def.field_label}
                      {def.is_required && <span className="text-destructive ml-1">*</span>}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="sticky left-0 bg-card z-10 font-medium whitespace-nowrap">
                      {p.full_name || <span className="italic text-muted-foreground">Unnamed</span>}
                    </TableCell>
                    <TableCell className="sticky left-0 bg-card z-10 text-sm text-muted-foreground whitespace-nowrap">
                      {p.email || "—"}
                    </TableCell>
                    {definitions.map((def) => (
                      <TableCell key={def.id} className="p-1">
                        {def.field_type === "boolean" ? (
                          <Select
                            value={editingValues[p.id]?.[def.id] || ""}
                            onValueChange={(v) => updateCellValue(p.id, def.id, v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">—</SelectItem>
                              <SelectItem value="true">Yes</SelectItem>
                              <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : def.field_type === "profile_link" ? (
                          <Select
                            value={editingValues[p.id]?.[def.id] || ""}
                            onValueChange={(v) => updateCellValue(p.id, def.id, v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">—</SelectItem>
                              {profiles
                                .filter((pp) => pp.id !== p.id)
                                .map((pp) => (
                                  <SelectItem key={pp.id} value={pp.id}>
                                    {pp.full_name || pp.email || pp.id}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        ) : def.field_type === "date" ? (
                          <Input
                            type="date"
                            className="h-8 text-xs"
                            value={editingValues[p.id]?.[def.id] || ""}
                            onChange={(e) => updateCellValue(p.id, def.id, e.target.value)}
                          />
                        ) : def.field_type === "number" ? (
                          <Input
                            type="number"
                            className="h-8 text-xs"
                            value={editingValues[p.id]?.[def.id] || ""}
                            onChange={(e) => updateCellValue(p.id, def.id, e.target.value)}
                            placeholder="0"
                          />
                        ) : (
                          <Input
                            type="text"
                            className="h-8 text-xs"
                            value={editingValues[p.id]?.[def.id] || ""}
                            onChange={(e) => updateCellValue(p.id, def.id, e.target.value)}
                            placeholder="—"
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Add/Edit Field Definition Dialog */}
      <Dialog open={defDialogOpen} onOpenChange={(open) => { if (!open) setDefDialogOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDef ? "Edit Field" : "Add Custom Field"}</DialogTitle>
            <DialogDescription>
              {editingDef
                ? "Update the custom field definition."
                : "Define a new custom field for user profiles. The field name will be used as a machine-readable key."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="def-label">Display Label *</Label>
              <Input
                id="def-label"
                value={defLabel}
                onChange={(e) => {
                  setDefLabel(e.target.value);
                  // Auto-generate field name from label if creating new
                  if (!editingDef) {
                    setDefName(
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9_\s]/g, "")
                        .replace(/\s+/g, "_")
                        .replace(/_+/g, "_")
                        .replace(/^_|_$/g, "")
                    );
                  }
                }}
                placeholder="e.g. Manager, City, Department"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="def-name">Field Name (key) *</Label>
              <Input
                id="def-name"
                value={defName}
                onChange={(e) => setDefName(e.target.value)}
                placeholder="e.g. manager_id, city, department"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Machine-readable key used in queries and CSV headers. Only lowercase letters, numbers, and underscores.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label>Field Type</Label>
              <Select value={defType} onValueChange={setDefType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {defType === "profile_link"
                  ? "Links to another user's profile (e.g. manager). Stored as profile ID."
                  : defType === "boolean"
                    ? "Yes/No toggle field."
                    : defType === "number"
                      ? "Numeric value (e.g. sales_target)."
                      : defType === "date"
                        ? "Date picker field."
                        : "Free-text field."}
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="def-required" className="text-sm font-medium">Required</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Mark this field as mandatory for all users.</p>
              </div>
              <Switch
                id="def-required"
                checked={defRequired}
                onCheckedChange={setDefRequired}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefDialogOpen(false)} disabled={defSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveDef} disabled={defSaving}>
              {defSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingDef ? "Save Changes" : "Create Field"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomUserFields;
